import React, {useState, useEffect} from 'react';
import {useParams, useNavigate} from 'react-router-dom';
import {
    Card,
    Typography,
    Input,
    Button,
    Form,
    Spin,
    App,
    Row,
    Col,
    Radio,
    InputNumber
} from 'antd';
import {v4 as uuidv4} from 'uuid';
import {authService, customerService} from '../../services/api';
import {usePageTitle} from '../../context/PageTitleContext';
import ErrorPage from "../ErrorPage/ErrorPage";
import HorizontalFormItem from "../../components/HorizontalItems/FormItem";

const {Title, Text} = Typography;

const CustomerEdit = () => {
    const {id} = useParams();
    const navigate = useNavigate();
    const {message} = App.useApp();
    const {setPageTitle, setRightContent} = usePageTitle();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [customer, setCustomer] = useState(null);
    const [saving, setSaving] = useState(false);
    const [correlationKey, setCorrelationKey] = useState(null);
    const [idempotencyKey, setIdempotencyKey] = useState(null);

    // Состояние для отслеживания предыдущего тела запроса
    const [previousBodyString, setPreviousBodyString] = useState('');

    useEffect(() => {
        fetchData();
        setPageTitle('Редактирование клиента');

        setRightContent(
            authService.hasPermission('pos:customers:edit') && (<Button
                type="primary"
                onClick={handleSave}
                loading={saving}
            >
                Сохранить
            </Button>)
        );

        return () => {
            setPageTitle('');
            setRightContent(null);
            setCustomer(null);
        };
    }, [setPageTitle, setRightContent, id]);

    useEffect(() => {
        if (customer) {
            const formattedCustomer = {
                ...customer,
                employee_day_balance: customer.employee_day_balance ? parseFloat(customer.employee_day_balance) : null
            };
            form.setFieldsValue(formattedCustomer);
        }
    }, [customer, form]);

    const fetchData = async () => {
        try {
            setLoading(true);

            const customerResponse = await customerService.getCustomerById(id);

            if (customerResponse.status === 'success') {
                setCustomer(customerResponse.data);
                console.log('Получены данные клиента:', customerResponse.data);
            } else {
                message.error('Не удалось загрузить данные клиента');
                navigate('/customers');
            }
        } catch (error) {
            if (error.response?.status === 404) {
                setError({
                    statusCode: 404,
                    title: "Клиент не найден",
                    description: "Указанный клиент не существует или был удален."
                });
            } else {
                setError({
                    statusCode: error.response?.status || 500,
                    title: "Ошибка загрузки данных",
                    description: "Произошла ошибка при загрузке данных клиента. Попробуйте обновить страницу или вернуться позже."
                });
            }
        } finally {
            setLoading(false);
        }
    };

    // Обработка сохранения данных клиента
    const handleSave = async () => {
        try {
            const values = await form.validateFields();


            // Преобразование данных перед отправкой
            const formattedValues = {
                ...values
            };

            console.log('Сохраняемые данные:', formattedValues);

            // Генерируем или обновляем ключ идемпотентности
            const currentBodyString = JSON.stringify(formattedValues);
            let currentIdempotencyKey = idempotencyKey;
            if (!currentIdempotencyKey || previousBodyString !== currentBodyString) {
                currentIdempotencyKey = uuidv4();
                setIdempotencyKey(currentIdempotencyKey);
                setPreviousBodyString(currentBodyString);
            }

            // Всегда генерируем новый ключ корреляции
            const newCorrelationKey = uuidv4();
            setCorrelationKey(newCorrelationKey);

            setSaving(true);

            // Передаем оба ключа в API
            const response = await customerService.saveCustomer(
                id,
                formattedValues,
                newCorrelationKey,
                currentIdempotencyKey
            );

            if (response.status === 'success') {
                message.success('Клиент успешно сохранен');
                fetchData(); // Обновляем данные после сохранения
            } else {
                message.error(response.message || 'Ошибка при сохранении клиента');
            }
        } catch (error) {
            console.error('Ошибка при сохранении:', error);
            message.error(error.message || 'Ошибка при сохранении клиента');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div style={{textAlign: 'center', padding: '50px 0'}}>
                <Spin size="large"/>
            </div>
        );
    }

    if (error) {
        return <ErrorPage
            statusCode={error.statusCode}
            title={error.title}
            description={error.description}
        />;
    }

    const formatFullName = (first_name, last_name) => {
        const parts = [first_name, last_name].filter(Boolean);
        return parts.length > 0 ? parts.join(' ') : 'Не указано';
    };

    return (
        <Card>
            <Title level={3} style={{marginTop: 0}}>
                Клиент «{formatFullName(customer.first_name, customer.last_name)}»
            </Title>
            <div style={{marginBottom: 24}}>
                <Text>ID: <Text strong>{customer.id}</Text></Text>
            </div>

            <Form
                form={form}
                layout="horizontal"
                initialValues={customer}
            >
                <Row gutter={24}>
                    <Col span={12}>
                        <Card title="Личные данные" style={{marginBottom: 24}}>
                            <HorizontalFormItem
                                label="Имя"
                                name="first_name"
                                rules={[
                                    {required: true, message: 'Введите имя'},
                                    {max: 255, message: 'Максимальная длина - 255 символов'}
                                ]}
                            >
                                <Input placeholder="Введите имя" maxLength={255}/>
                            </HorizontalFormItem>

                            <HorizontalFormItem
                                label="Фамилия"
                                name="last_name"
                                rules={[
                                    {required: true, message: 'Введите фамилию'},
                                    {max: 255, message: 'Максимальная длина - 255 символов'}
                                ]}
                            >
                                <Input placeholder="Введите фамилию" maxLength={255}/>
                            </HorizontalFormItem>

                            <HorizontalFormItem
                                label="Телефон"
                                name="phone"
                                rules={[
                                    {max: 20, message: 'Максимальная длина - 20 символов'}
                                ]}
                            >
                                <Input placeholder="Введите телефон" maxLength={20}/>
                            </HorizontalFormItem>

                            <HorizontalFormItem
                                label="Email"
                                name="email"
                                rules={[
                                    {type: 'email', message: 'Введите корректный email'},
                                    {max: 255, message: 'Максимальная длина - 255 символов'}
                                ]}
                            >
                                <Input placeholder="Введите email" maxLength={255}/>
                            </HorizontalFormItem>
                        </Card>
                    </Col>
                    <Col span={12}>
                        <Card title="Системная информация" style={{marginBottom: 24}}>
                            <HorizontalFormItem
                                label="Сотрудник"
                                name="is_employee"
                                rules={[
                                    {required: true, message: 'Выберите статус сотрудника'}
                                ]}
                            >
                                <Radio.Group>
                                    <Radio.Button value={1}>Да</Radio.Button>
                                    <Radio.Button value={0}>Нет</Radio.Button>
                                </Radio.Group>
                            </HorizontalFormItem>

                            <Form.Item
                                noStyle
                                shouldUpdate={(prevValues, currentValues) =>
                                    prevValues.is_employee !== currentValues.is_employee
                                }
                            >
                                {({getFieldValue}) =>
                                    getFieldValue('is_employee') === 1 ? (
                                        <HorizontalFormItem
                                            label="Баланс сотрудника на день"
                                            name="employee_day_balance"
                                            rules={[
                                                {required: true, message: 'Введите баланс'},
                                                {type: 'number', min: 0, message: 'Баланс не может быть отрицательным'}
                                            ]}
                                        >
                                            <InputNumber
                                                style={{width: '100%'}}
                                                placeholder="Введите баланс"
                                                step={1.00}
                                                precision={2}
                                            />
                                        </HorizontalFormItem>
                                    ) : null
                                }
                            </Form.Item>
                        </Card>
                    </Col>
                </Row>
            </Form>
        </Card>
    );
}

export default CustomerEdit;